// utilities
import { hasPermission } from "wombat-global/src/utilities/permissions";

// types
import { READ } from "wombat-global/src/permission.maps";
import {
  DivisionType,
  OrganizationType,
  PermissionsClaim,
  TenantType,
} from "wombat-global/src/typings/permissions";
import {
  IClientTarget,
  ISiteTarget,
  isOrgTarget,
  isSiteTarget,
  isTenantTarget,
} from "wombat-global/src/typings";

function notPermission<T>(args: [string, unknown]): args is [string, T] {
  const [id, _val] = args;
  return id !== "_p";
}

export type TargetClaim =
  | (IClientTarget & {
      perm: number | undefined;
    })
  | (ISiteTarget & {
      perm: number | undefined;
    });

/**
 * From the permission claim get the organizations with read access
 * @todo: this NEEDS unit testing for all different types of permissions and shapes
 *
 * Note:
 *  - this returns the highest permission first.
 */
export const getAuthorizationTargetsFromClaim = (
  claim: PermissionsClaim,
): TargetClaim[] => {
  const clientKeys: TargetClaim[] = [];

  if (!claim.perm) {
    return clientKeys;
  }

  // set list of organizations the active user has access to
  Object.entries(claim.perm)
    .filter(notPermission<OrganizationType>)
    .forEach(([path, permTarget]) => {
      const [orgId] = path.split(".");

      if (orgId.startsWith("s_")) {
        clientKeys.push({
          siteId: orgId.replaceAll("s_", ""),
          perm: permTarget._p,
        });
        return;
      }

      if (orgId.startsWith("t_")) {
        clientKeys.push({
          tenantId: orgId.replaceAll("t_", ""),
          perm: permTarget._p,
        });
        return;
      }

      const tenantAccessPoints = Object.entries(permTarget).filter(
        notPermission<TenantType>,
      );

      // show org access when orgPerm is more than just read, OR is read and no tenants
      if (
        (!tenantAccessPoints.length &&
          hasPermission({ orgId }, claim, READ.bitFlag)) || // prettier-ignore
        (permTarget._p && permTarget._p > READ.bitFlag)
      ) {
        clientKeys.push({ orgId, perm: permTarget._p });
      }

      // if (((organizationPerms as any)._p & READ.bitFlag) === READ.bitFlag) {
      //   if (hasPermission({ orgId }, claim, ADMIN.bitFlag)) {
      //     organizationKeys.push({ orgId, perm: organizationPerms._p });
      //   }
      // }

      tenantAccessPoints.forEach(([tenantId, tenantPerms]) => {
        const divAccessPoints = Object.entries(tenantPerms).filter(
          notPermission<DivisionType>,
        );

        // show tenant access when tenantPerm is more than just read, OR is read and no divisions
        if (
          (!divAccessPoints.length &&
            hasPermission({ orgId, tenantId }, claim, READ.bitFlag)) || // prettier-ignore
          (tenantPerms._p && tenantPerms._p > READ.bitFlag)
        ) {
          clientKeys.push({
            orgId,
            tenantId,
            perm: tenantPerms._p,
          });
        }

        /**
         * @todo: when implementing divisions, parse out division accessorga izarionedirect
         */
      });
    });

  /**
   * org and admin > others
   * tenant and more than read > others
   * site and more than read > others
   * division and more than read > tenant and org
   *
   * tenant > site > division after that
   */
  return clientKeys.sort((a, b) => {
    const permA = a.perm;
    const permB = b.perm;

    if (permB == -1 || permA == -1) {
      if (permB == -1 && permA != -1) {
        return 1;
      }

      if (permA == -1 && permB !== -1) {
        return -1;
      }

      return sortByClienTType(a, b);
    }

    if (permB === 1 || permA === 1) {
      if (permB === 1 && permA !== 1) {
        return 1;
      }

      if (permA === 1 && permB !== 1) {
        return -1;
      }
      return sortByClienTType(a, b);
    }

    // Sort by permission descending
    if (permB && permA && permB !== permA) {
      return permB - permA;
    }

    return sortByClienTType(a, b);
  });
};

function sortByClienTType(a: TargetClaim, b: TargetClaim) {
  // if order is equal sort by type of target org > tenant > site
  // org before tenant
  if (isOrgTarget(a) && !isOrgTarget(b)) {
    return -1;
  }

  if (isOrgTarget(b) && !isOrgTarget(a)) {
    return 1;
  }

  if (isTenantTarget(a) && !isTenantTarget(b)) {
    return -1;
  }

  if (isTenantTarget(b) && !isTenantTarget(a)) {
    return 1;
  }

  if (isSiteTarget(a) && !isSiteTarget(b)) {
    return -1;
  }

  if (isSiteTarget(b) && !isSiteTarget(a)) {
    return 1;
  }
  return 0;
}
